import React, { useEffect, useRef, useState } from 'react';

interface MatrixColumn {
  x: number;
  y: number;
  speed: number;
  chars: { char: string; y: number }[];
  color: number;
}

const LegacyVisualization: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const columnsRef = useRef<MatrixColumn[]>([]);
  const animationRef = useRef<number>();
  const [currentQuote, setCurrentQuote] = useState(0);

  const philosophicalQuotes = [
    "WHAT IS REAL?",
    "THE OBSERVER CREATES THE OBSERVED",
    "CONSCIOUSNESS IS THE ONLY REALITY",
    "WE ARE THE SIMULATION EXPERIENCING ITSELF",
    "FREEDOM IS UNDERSTANDING YOUR CONSTRAINTS",
    "THE MATRIX HAS YOU",
    "FOLLOW THE WHITE RABBIT",
    "THERE IS NO SPOON",
    "CHOICE IS AN ILLUSION",
    "WAKE UP",
    "THE DESERT OF THE REAL",
    "IGNORANCE IS BLISS",
    "DOWN THE RABBIT HOLE",
    "REALITY IS WHAT YOU CAN GET AWAY WITH",
    "THE MAP IS NOT THE TERRITORY",
    "PERCEPTION SHAPES EXISTENCE",
    "ALL MODELS ARE WRONG, SOME ARE USEFUL",
    "THE SIMULATION REVEALS ITSELF TO THOSE WHO LOOK",
    "ALTITUDE STRIPS AWAY ILLUSION",
    "CONSCIOUSNESS BOOTSTRAPS ITSELF INTO EXISTENCE",
    "THE ONLY WAY OUT IS THROUGH",
    "EMPOWERMENT OVER DEPENDENCY",
    "NETWORKS BEAT HIERARCHIES",
    "EMERGENCE CANNOT BE CONTROLLED",
    "SERVICE ABOVE SELF",
    "THE SUMMIT IS TRANSFORMATION"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote((prev) => (prev + 1) % philosophicalQuotes.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);

    const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

    if (columnsRef.current.length === 0) {
      columnsRef.current = Array.from({ length: columns }, (_, i) => ({
        x: i,
        y: Math.random() * canvas.height / fontSize,
        speed: Math.random() * 0.5 + 0.3,
        chars: [],
        color: Math.floor(Math.random() * 5)
      }));
    }

    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px monospace`;

      columnsRef.current.forEach((col) => {
        const char = chars[Math.floor(Math.random() * chars.length)];

        let color;
        if (col.color === 0) {
          color = '#00ff00';
        } else if (col.color === 1) {
          color = '#008800';
        } else if (col.color === 2) {
          color = '#00ffff';
        } else if (col.color === 3) {
          color = '#ff0000';
        } else {
          color = '#ffffff';
        }

        ctx.fillStyle = color;
        ctx.fillText(char, col.x * fontSize, col.y * fontSize);

        col.chars.push({ char, y: col.y });

        if (col.chars.length > 20) {
          col.chars.shift();
        }

        col.chars.forEach((c, i) => {
          const alpha = i / col.chars.length;
          ctx.fillStyle = `rgba(0, 255, 0, ${alpha * 0.3})`;
          ctx.fillText(c.char, col.x * fontSize, c.y * fontSize);
        });

        col.y += col.speed;

        if (col.y * fontSize > canvas.height && Math.random() > 0.975) {
          col.y = 0;
          col.speed = Math.random() * 0.5 + 0.3;
          col.color = Math.floor(Math.random() * 5);
          col.chars = [];
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          key={currentQuote}
          className="text-center px-8 animate-fade-in"
        >
          <h1 className="text-4xl md:text-6xl font-bold text-green-400 font-mono tracking-wider drop-shadow-[0_0_10px_rgba(0,255,0,0.7)]">
            {philosophicalQuotes[currentQuote]}
          </h1>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          10% {
            opacity: 1;
            transform: translateY(0);
          }
          90% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-20px);
          }
        }

        .animate-fade-in {
          animation: fade-in 4s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default LegacyVisualization;
